import React, { useState } from 'react'
import { Form, Formik } from 'formik'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  makeStyles,
  Slider,
  Typography,
  TextField,
  MenuItem,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'

import yup from 'civic-champs-shared/utils/yup'
import { ContainedButton, OutlinedButton } from 'civic-champs-shared/core'
import StyledInput from 'civic-champs-shared/core/StyledInput'
import { StyledDateTimePicker } from 'civic-champs-shared/formik/components/StyledDateTimePickers'

const useStyles = makeStyles({
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sliderContainer: {
    padding: '0 12px',
  },
  sliderLabel: {
    fontSize: '12px',
    color: '#666',
    marginBottom: '8px',
  },
  sliderValue: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#333',
  },
})

interface LoadSimulatorModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (values: LoadSimulatorPayload) => Promise<void>
}

export interface LoadSimulatorPayload {
  durationInMs: number
  batchSize: number
  priority: number
  failureRate: number
  beginLoadAt?: Date
  minEventDelay: number
  maxEventDelay: number
}

const validationSchema = yup.object({
  durationInMs: yup.number().integer().min(0).max(300000).required('Duration is required'),
  batchSize: yup.number().integer().min(1).max(500).required('Batch size is required'),
  priority: yup.number().integer().min(1).max(3).required('Priority is required'),
  failureRate: yup.number().min(0).max(1).required('Failure rate is required'),
  beginLoadAt: yup.date().nullable(),
  minEventDelay: yup.number().integer().min(0).max(300000).required('Min delay is required'),
  maxEventDelay: yup
    .number()
    .integer()
    .min(0)
    .max(300000)
    .test('max-gte-min', 'Max delay must be >= min delay', function (value) {
      return value >= this.parent.minEventDelay
    })
    .required('Max delay is required'),
})

const priorityOptions = [
  { value: 1, label: 'High Priority' },
  { value: 2, label: 'Normal Priority' },
  { value: 3, label: 'Low Priority' },
]

export const LoadSimulatorModal: React.FC<LoadSimulatorModalProps> = ({ open, onClose, onSubmit }) => {
  const classes = useStyles()
  const [submitting, setSubmitting] = useState(false)

  const initialValues: LoadSimulatorPayload = {
    durationInMs: 5000,
    batchSize: 10,
    priority: 2,
    failureRate: 0,
    beginLoadAt: undefined,
    minEventDelay: 0,
    maxEventDelay: 0,
  }

  const handleSubmit = async (values: LoadSimulatorPayload) => {
    setSubmitting(true)
    try {
      await onSubmit(values)
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ values, errors, touched, setFieldValue }) => (
          <Form>
            <DialogTitle>
              <div className={classes.title}>
                <Typography variant="h6">Simulate EventHandler Load</Typography>
                <IconButton onClick={onClose} size="small">
                  <CloseIcon />
                </IconButton>
              </div>
            </DialogTitle>

            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <div className={classes.sliderContainer}>
                    <div className={classes.sliderLabel}>
                      Duration per Event: <span className={classes.sliderValue}>{values.durationInMs}ms</span>
                    </div>
                    <Slider
                      value={values.durationInMs}
                      onChange={(_, value) => setFieldValue('durationInMs', value)}
                      min={0}
                      max={300000}
                      step={1000}
                      valueLabelDisplay="auto"
                      valueLabelFormat={value => `${value}ms`}
                    />
                  </div>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <StyledInput
                    label="Batch Size"
                    value={String(values.batchSize)}
                    onChange={value => setFieldValue('batchSize', parseInt(value) || 0)}
                    error={touched.batchSize ? errors.batchSize : undefined}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Priority"
                    value={values.priority}
                    onChange={e => setFieldValue('priority', e.target.value)}
                    variant="outlined"
                  >
                    {priorityOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <div className={classes.sliderContainer}>
                    <div className={classes.sliderLabel}>
                      Failure Rate: <span className={classes.sliderValue}>{(values.failureRate * 100).toFixed(0)}%</span>
                    </div>
                    <Slider
                      value={values.failureRate}
                      onChange={(_, value) => setFieldValue('failureRate', value)}
                      min={0}
                      max={1}
                      step={0.05}
                      valueLabelDisplay="auto"
                      valueLabelFormat={value => `${(value * 100).toFixed(0)}%`}
                    />
                  </div>
                </Grid>

                <Grid item xs={12}>
                  <StyledDateTimePicker
                    label="Begin Load At (optional)"
                    value={values.beginLoadAt}
                    onChange={date => setFieldValue('beginLoadAt', date)}
                    clearable
                    helperText="Leave empty to start immediately"
                    inputVariant="outlined"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <StyledInput
                    label="Min Event Delay (ms)"
                    value={String(values.minEventDelay)}
                    onChange={value => setFieldValue('minEventDelay', parseInt(value) || 0)}
                    error={touched.minEventDelay ? errors.minEventDelay : undefined}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <StyledInput
                    label="Max Event Delay (ms)"
                    value={String(values.maxEventDelay)}
                    onChange={value => setFieldValue('maxEventDelay', parseInt(value) || 0)}
                    error={touched.maxEventDelay ? errors.maxEventDelay : undefined}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <OutlinedButton onClick={onClose} disabled={submitting}>
                Cancel
              </OutlinedButton>
              <ContainedButton type="submit" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit'}
              </ContainedButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}
